import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { ColumnResizeDragOverlay, ColumnResizeHandle } from "../../../../../components/atoms/Table";
import {
  DETAIL_TOTALS_FOOTER_BG,
  DETAIL_TOTALS_FOOTER_SHADOW,
  detailTotalsFooterBarTopCss,
  detailTotalsFooterLabelCss,
  detailTotalsFooterRowCss,
  detailTotalsFooterValueCss,
} from "../../../../../components/atoms/Table/detailTotalsFooter.styles";
import {
  tableCellTextCss,
  tableHeaderTextCss,
  tableNumericCss,
} from "../../../../../components/atoms/Table/tableTypography.styles";
import { measureHeaderMinWidth } from "../../../../../helper/columnWidthConstraints";
import { isNestedTagColumnUnsortable } from "../../../../../helper/listColumns";
import { measureColumnFitFromDom } from "../../../../../helper/measureColumnFit";
import { trackEvent } from "../../../../../helper/trackEvent";
import { useColumnResize } from "../../../../../hooks/useColumnResize";
import { useFormattedAmount } from "../../../../../hooks/useFormattedAmount";
import { useUpdateColumnWidthInListMutation } from "../../../../../store/apiSlice/newInterfaceApiSlice";
import { useCreateItemsPerPageMutation } from "../../../../../store/apiSlice/userApiSlice";
import { setColumnWidthForDefaultViewInLocals } from "../../../../../store/slice/newInterfaceSlice";
import { resetConsignmentsSelection } from "../../../../../store/slice/shipmentSlice";
import { SALE_INVOICE_FULFILLMENT_FILTER_HEADERS } from "../../../../saleInvoice/layout/constant";
import { ReactComponent as Close } from "../../../assets/svgIcon/listView/Close.svg";
import { ReactComponent as FilterIcon } from "../../../assets/svgIcon/listView/filter.svg";
import { UpDown } from "../../../assets/svgIcon/listView/up-down-arrow";
import { PopupModal } from "../../../components/TopBar/PopupModal/PopupModal";
import { ShowTextOnHover, TextOnHoverWhenAdjust } from "../../ToolTip/ToolTip";
import { listRowIdentity } from "../../Constants/util";
import { readListPageSize, writeListPageSize } from "../../utils/listPageSizePreference";
import {
  classNameForHeader,
  COLUMN_CONSTRAINTS,
  countMapWithHeader,
  detailPageUrl,
  headersNameForFilter,
  headersNameForFilterByModule,
  listOfClasses,
  listOfClassesForHeader,
  mapDateFilterId,
  mapItemPerPageWithModule,
  qtyItemsForAllocation,
  tableDataElements,
  totalCountForData,
} from "../../utils/tableDataElements";
import { SelectSortOption } from "../SelectSortOption";
import { EmptyStateComp } from "./EmptyState/EmptyStateComp";
import { clearListFilters, getEmptyStateContent } from "./EmptyState/emptyStateCopy";
import {
  DateTimeFilterHeaderV2,
  getFallbackDateFilterParamKeys,
} from "./FilterModal/DateTimeFilterHeaderV2";
import { FilterModal } from "./FilterModal/FilterModal";
import { SearchFilterModal } from "./FilterModal/SearchFilterModal";
import { getSearchFilterConfig, isSearchFilterField } from "./FilterModal/searchFilterConfig";
import { ListBoardPagination } from "./ListBoardPagination";
import {
  booleanField,
  dataForSort,
  dateFields,
  getListHeaderDisplayLabel,
  keysToRemoveOnClearFilter,
  nameOfFields,
  orderingFieldByModule,
  qtyVal,
} from "./util";

/**
 * Module-specific display header label overrides.
 *
 * @const
 * @type {!Object<string, !Object<string, string>>}
 */
const HEADER_DISPLAY_OVERRIDES = {
  supplier: { "CO MANUFACTURER": "PARTNER" },
  prePayment: { "PREPAYMENT NO": "PAYMENT NO" },
};

/**
 * Header sort icon color states.
 *
 * @const
 * @type {!Object<string, string>}
 */
const SORT_ICON_COLORS = {
  ACTIVE: "#2060ea",
  INACTIVE: "#676879",
};

/**
 * ListView Component
 *
 * A highly customizable and reusable table component for displaying tabular data
 * such as shipment orders, allocations, inventories, etc., with advanced features
 * like sorting, filtering, pagination, column resizing, and selection.
 *
 * @component
 * @param {Object} props
 * @param {Array} props.shipmentOrders - List of order data to render in rows.
 * @param {Array} props.tableHeaderList - List of column headers to display.
 * @param {Function} props.handleSelectOrder - Handler to select/deselect a row or all rows.
 * @param {string} props.orderType - The type of data being displayed ("shipment", "allocation", etc.).
 * @param {string} props.allocation - Optional allocation type ("transfer", etc.).
 * @param {Array} [props.selectedOrderFromList=[]] - Selected rows.
 * @param {Function} props.setConsignmentsParamsLocal - Function to update filter/pagination parameters.
 * @param {Object} [props.consignmentsParams={ limit: 100, offset: 0 }] - Pagination/filtering query parameters.
 * @param {number} props.totalItems - Total number of records (used for pagination).
 * @param {number} [props.currentPage] - Optional controlled one-based page number.
 * @param {Object} props.widthForHeaders - Column width settings per header.
 * @param {Object} props.selectedCurrentView - View metadata including `id` used for saving layout settings.
 * @param {Function} [props.onTrackingUpdated] - Refetch list after ocean tracking changes (shipment list).
 * @returns {React.ReactElement}
 */

const ListView = ({
  shipmentOrders,
  tableHeaderList,
  handleSelectOrder,
  orderType,
  allocation,
  selectedOrderFromList = [],
  setConsignmentsParamsLocal,
  consignmentsParams = { limit: 100, offset: 0 },
  currentPage,
  totalItems,
  widthForHeaders,
  selectedCurrentView,
  onTrackingUpdated,
  onOpenIncomingDrawer,
  recordActionHandlers,
}) => {
  const [selectedOrder, setSelectedOrder] = useState(shipmentOrders || []);
  const [positionForFilter, setPositionForFilter] = useState(null);
  const [currentHeaderForSorting, setCurrentHeaderForSorting] = useState(null);
  const emptyColRef = useRef(null);
  const tableRef = useRef(null);
  const [updateColumnWidth] = useUpdateColumnWidthInListMutation();
  const [updatePerPage] = useCreateItemsPerPageMutation();

  const [columnHeaderWidth, setColumnHeaderWidth] = useState(widthForHeaders || {});
  const headerRefs = useRef({});
  const afterFirstRenderColumnWidth = useRef(null);
  const formattedAmount = useFormattedAmount();

  const [isSortModalOpen, setIsSortModalOpen] = useState({
    eta: { isOpen: false, value: null, isAppliedSorting: false },
    status: { isOpen: false, value: null, isAppliedSorting: false },
    "product no": { isOpen: false, value: null, isAppliedSorting: false },
  });
  const dispatch = useDispatch();
  const initialStateForFilter = { isOpen: false, isApplied: false };
  const [isAppliedFilter, setIsAppliedFilter] = useState({
    "shipment no": initialStateForFilter,
    "shipment name": initialStateForFilter,
  });
  const viewOnly = useSelector(
    state =>
      Boolean(state.auth.user?.groups?.length) &&
      state.auth.user.groups.every(group => group.name === "Viewer"),
  );

  const clearAllFilters = useCallback(() => {
    dispatch(resetConsignmentsSelection());
    setIsAppliedFilter({
      "shipment no": { isOpen: false, isApplied: false },
      "shipment name": { isOpen: false, isApplied: false },
    });
    setConsignmentsParamsLocal(state => clearListFilters(state));
  }, [dispatch, setConsignmentsParamsLocal]);

  const emptyState = useMemo(
    () =>
      getEmptyStateContent({
        orderType,
        params: consignmentsParams,
        view: selectedCurrentView,
        viewOnly,
        onClearFilters: clearAllFilters,
      }),
    [orderType, consignmentsParams, selectedCurrentView, viewOnly, clearAllFilters],
  );
  const isListEmpty = selectedOrder.length === 0;

  useEffect(() => {
    if (isListEmpty) {
      trackEvent("empty_state_shown", { empty_variant: emptyState.variant, list: orderType });
    }
  }, [isListEmpty, emptyState.variant, orderType]);

  const selectedOrderId = useMemo(
    () => selectedOrderFromList.map(listRowIdentity),
    [selectedOrderFromList],
  );

  const clearFilter = headerName => {
    dispatch(resetConsignmentsSelection());
    const storedKeys = isAppliedFilter[headerName]?.paramKeys;
    const searchConfig = getSearchFilterConfig(orderType, headerName);
    const appliedKeys =
      Array.isArray(storedKeys) && storedKeys.length > 0
        ? storedKeys
        : searchConfig
          ? [searchConfig.paramKey]
          : dateFields.includes(headerName) ||
              ["create date", "date of transaction", "req date"].includes(headerName)
            ? getFallbackDateFilterParamKeys(headerName)
            : [];

    const keysToRemove = keysToRemoveOnClearFilter({
      headerName,
      isAppliedFilter,
      storedKeys: appliedKeys,
    });

    setConsignmentsParamsLocal(state => {
      const newParams = { ...state };
      keysToRemove.forEach(key => {
        delete newParams[key];
      });
      newParams.offset = 0;
      return newParams;
    });
    setIsAppliedFilter(state => {
      return {
        ...state,
        [headerName]: { isOpen: false, isApplied: false, paramKeys: [], value: "" },
      };
    });
  };

  const closeFilterModal = headerName => {
    // Closing the popup must not clear applied state / paramKeys — otherwise the
    // clear (X) control disappears while query params remain active.
    setIsAppliedFilter(state => ({
      ...state,
      [headerName]: {
        ...state[headerName],
        isOpen: false,
      },
    }));
    setPositionForFilter(null);
  };

  const isFilterable = useCallback(
    header => {
      if (
        orderType === "saleInvoice" &&
        SALE_INVOICE_FULFILLMENT_FILTER_HEADERS.includes(String(header).toLowerCase())
      ) {
        return true;
      }
      return [
        "asn",
        "product",
        "chartsOfAccount",
        "paymentTerm",
        "billChargeType",
        "journalLine",
        "productTaxClassification",
        "customerSegment",
        "taxRate",
        "taxCategory",
        "shopifyCatalogue",
        "shopifyLinkedProducts",
        "pricelist",
        "workCenter",
        "shipmentLeg",
        "people",
        "users",
        "customer",
        "supplier",
        "coManufacturingOrder",
        "inventory",
        "inventoryDetail",
        "inventoryTransactions",
        "warehouse",
        "warehouseCosting",
      ].includes(orderType)
        ? headersNameForFilterByModule[orderType].filter.includes(header)
        : headersNameForFilter.includes(header);
    },
    [orderType],
  );

  const isSortable = useCallback(
    header => {
      if (isNestedTagColumnUnsortable(header)) return false;

      return [
        "asn",
        "product",
        "chartsOfAccount",
        "paymentTerm",
        "billChargeType",
        "journalLine",
        "productTaxClassification",
        "customerSegment",
        "taxRate",
        "taxCategory",
        "shopifyCatalogue",
        "shopifyLinkedProducts",
        "pricelist",
        "workCenter",
        "shipmentLeg",
        "people",
        "users",
        "customer",
        "supplier",
        "coManufacturingOrder",
        "inventory",
        "inventoryDetail",
        "inventoryTransactions",
        "warehouse",
        "warehouseCosting",
      ].includes(orderType)
        ? headersNameForFilterByModule[orderType].sort.includes(header)
        : headersNameForFilter.includes(header);
    },
    [orderType],
  );

  useEffect(() => {
    if (shipmentOrders) setSelectedOrder(shipmentOrders);
  }, [shipmentOrders]);

  useEffect(() => {
    if (widthForHeaders && Object.keys(widthForHeaders).length > 0) {
      setColumnHeaderWidth(widthForHeaders);
    } else if (!selectedCurrentView?.id) {
      setColumnHeaderWidth({});
    }
  }, [selectedCurrentView?.id, widthForHeaders]);

  useEffect(() => {
    const handleMeasureHeaderWidths = () => {
      const headerFields = Object.keys(headerRefs.current);
      if (
        tableHeaderList &&
        headerFields.length > 0 &&
        typeof headerRefs.current[headerFields[0]] === "object" &&
        headerRefs.current[headerFields[0]] !== null
      ) {
        let headersWidth = {};
        headerFields.forEach(header => {
          if (
            typeof headerRefs.current[header] === "object" &&
            headerRefs.current[header] !== null
          ) {
            const w = headerRefs.current[header].getBoundingClientRect().width;
            if (w > 0) {
              headersWidth[header] = Math.ceil(w);
            }
          }
        });
        afterFirstRenderColumnWidth.current = headersWidth;

        setColumnHeaderWidth(prev => ({
          ...headersWidth,
          ...widthForHeaders,
          ...prev,
        }));
      }
    };

    const frameId = requestAnimationFrame(handleMeasureHeaderWidths);
    return () => cancelAnimationFrame(frameId);
  }, [tableHeaderList, shipmentOrders, widthForHeaders]);

  let totalCount = useMemo(() => {
    let count = null;
    if (orderType === "allocation") count = totalCountForData[orderType](selectedOrder);
    return count;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrder]);
  const demoFn = () => "";

  const handleEtaSort = header => {
    setIsSortModalOpen({
      ...isSortModalOpen,
      [header]: { ...isSortModalOpen[header], isOpen: true },
    });
  };
  const handleFilterModalOpen = (header, e) => {
    // Keep isApplied / paramKeys when reopening so Cancel/close does not strand
    // active query params without a clear control.
    setIsAppliedFilter(state => ({
      ...state,
      [header]: { ...state[header], isOpen: true },
    }));
    if (e?.clientX > window.innerWidth / 2) {
      setPositionForFilter({ right: 0 });
    }
  };

  /**
   * Modules in orderingFieldByModule sort via the list API `ordering`
   * param. Other modules keep client-side sort.
   */
  const applyServerSort = (filteredBy, valueOfCol) => {
    const orderTypeForSort = orderType === "allocation" ? allocation + "Allocation" : orderType;
    const fieldMap = orderingFieldByModule[orderTypeForSort];
    if (!fieldMap) return false;

    const apiField = fieldMap[valueOfCol];
    if (filteredBy !== "reset" && !apiField) return false;

    setConsignmentsParamsLocal(state => {
      const next = { ...state, offset: 0 };
      if (filteredBy === "reset") {
        delete next.ordering;
        return next;
      }
      const descending = ["Descending", "Newest to Oldest"].includes(filteredBy);
      next.ordering = descending ? `-${apiField}` : apiField;
      return next;
    });
    return true;
  };

  const handleSort = {
    eta: (filteredBy, valueOfCol) => {
      if (applyServerSort(filteredBy, valueOfCol)) return;

      const arr = [...shipmentOrders];
      let nameOfField = nameOfFields[valueOfCol];

      if (orderType === "purchase" && valueOfCol === "eta") {
        nameOfField = "estimated_date";
      } else if (
        [
          "product",
          "productCategory",
          "productAlias",
          "pack",
          "forecast",
          "warehouseCosting",
          "warehouseCostingRange",
          "stockZone",
          "stockLocation",
          "transactionReason",
          "standardCost",
          "taxRate",
          "site",
          "workCenter",
          "people",
          "users",
          "boms",
          "customerTag",
          "prePayment",
          "stockReplenishment",
          "customer",
          "salesRep",
          "inventoryDetail",
        ].includes(orderType) &&
        (valueOfCol === "create date" || valueOfCol === "joined")
      ) {
        nameOfField = "date_of_creation";
      } else if (orderType === "users" && valueOfCol === "last login") {
        nameOfField = "last_login";
      } else if (orderType === "salesOrder" && valueOfCol === "order date") {
        nameOfField = "order_date";
      }

      if (filteredBy === "reset") {
        setSelectedOrder(shipmentOrders);
        return;
      }

      if (filteredBy === "Oldest to Newest") {
        arr.sort((a, b) => new Date(a[nameOfField]) - new Date(b[nameOfField]));
      } else {
        arr.sort((a, b) => new Date(b[nameOfField]) - new Date(a[nameOfField]));
      }

      setSelectedOrder(arr);
    },

    status: (filteredBy, valueOfCol) => {
      if (applyServerSort(filteredBy, valueOfCol)) return;

      const arr = [...shipmentOrders];
      const nameOfField = nameOfFields[valueOfCol];

      const selectValue = item => {
        if (orderType === "product") {
          switch (valueOfCol) {
            case "number":
              return item.number;
            case "name":
              return item.name;
            case "type":
              return item.type?.name || "";
            case "created by":
              return item.created_by?.full_name;
            case "status":
              return item.status?.name;
            default:
              return item[nameOfField] || "";
          }
        } else if (valueOfCol === "supplier" && orderType === "shipment") {
          return item.suppliers[0]?.name || "";
        } else if (valueOfCol === "name" && orderType === "shipment") {
          return item.reference || "";
        } else if (valueOfCol === "from facility" && orderType === "shipment") {
          return item.from_warehouses[0]?.name || "";
        } else if (valueOfCol === "created by" && orderType === "shipment") {
          return item.created_by?.full_name || "";
        } else if (valueOfCol === "scope" && orderType === "pack") {
          return [
            item.scope_purchase && "Purchase",
            item.scope_sales && "Sales",
            item.scope_inventory && "Inventory",
            item.scope_production && "Production",
          ]
            .filter(Boolean)
            .join(", ");
        } else if (
          valueOfCol === "from facility" &&
          orderType === "allocation" &&
          allocation === "transfer"
        ) {
          return item.transfer_order?.from_warehouse?.name || "";
        } else if (
          valueOfCol === "to facility" &&
          orderType === "allocation" &&
          allocation === "transfer"
        ) {
          return item.transfer_order?.to_warehouse || "";
        } else if (
          valueOfCol === "ord no" &&
          orderType === "allocation" &&
          allocation === "transfer"
        ) {
          return item.transfer_order?.name || "";
        } else if (valueOfCol === "order name" && orderType === "allocation") {
          return item.purchase_order?.user_reference || "";
        } else if (valueOfCol === "ord no" && orderType === "allocation") {
          return item.purchase_order?.name || "";
        } else if (valueOfCol === "supplier" && orderType === "allocation") {
          return item.purchase_order?.supplier || "";
        } else if (valueOfCol === "supplier" && orderType === "purchase") {
          return item.supplier || "";
        } else if (orderType === "purchase" && valueOfCol === "payment status") {
          return item.payment_status?.name || item.payment_status?.id || "";
        } else if (orderType === "purchase" && valueOfCol === "payments") {
          return item.total_payments_allocated ?? "";
        } else if (orderType === "purchase" && valueOfCol === "outstanding") {
          return item.payment_outstanding ?? "";
        } else if (orderType === "purchase" && valueOfCol === "po total") {
          return item.total_amount ?? "";
        } else if (valueOfCol === "supplier") {
          return item?.supplier?.name || "";
        } else if (valueOfCol === "to facility" && orderType === "allocation") {
          return item.purchase_order?.to_warehouse || "";
        } else if (
          valueOfCol === "product" &&
          [
            "allocation",
            "inventory",
            "productAlias",
            "pack",
            "forecast",
            "warehouseCosting",
            "productPlan",
            "standardCost",
            "stockReplenishment",
            "boms",
            "manufacturingOrder",
          ].includes(orderType)
        ) {
          return item.product?.number || "";
        } else if (valueOfCol === "product name" && orderType === "boms") {
          return item.product?.name || "";
        } else if (valueOfCol === "product" && orderType === "inventoryDetail") {
          return item?.stock_summary?.product?.number || "";
        } else if (valueOfCol === "description" && orderType === "inventoryDetail") {
          return item?.stock_summary?.product?.name || "";
        } else if (valueOfCol === "facility" && orderType === "inventoryDetail") {
          return item?.stock_summary?.warehouse?.name || "";
        } else if (valueOfCol === "product" && orderType === "inventoryTransactions") {
          return item?.product_number || "--";
        } else if (valueOfCol === "facility" && orderType === "inventoryTransactions") {
          return item?.warehouse_name || "--";
        } else if (valueOfCol === "reason" && orderType === "inventoryTransactions") {
          return item?.reason?.reason || "--";
        } else if (valueOfCol === "facility" && orderType === "stockLocation") {
          return item?.stock_zone?.warehouse?.name || "";
        } else if (valueOfCol === "status" && orderType === "transfer") {
          return item?.status?.name || "";
        } else if (
          valueOfCol === "created by" &&
          [
            "productCategory",
            "productTag",
            "warehouse",
            "productAlias",
            "stockZone",
            "stockLocation",
            "forecast",
            "warehouseCosting",
            "warehouseCostingRange",
            "inventoryDetail",
            "taxRate",
            "site",
            "workCenter",
            "people",
            "boms",
            "salesRep",
            "supplier",
          ].includes(orderType)
        ) {
          return item?.created_by?.full_name || "";
        } else if (orderType === "users" && valueOfCol === "name") {
          return item?.full_name || item?.email || "";
        } else if (orderType === "users" && valueOfCol === "email") {
          return item?.email || "";
        } else if (orderType === "users" && valueOfCol === "role") {
          return (item?.groups || []).map(g => g?.name).filter(Boolean).join(", ");
        } else if (orderType === "users" && valueOfCol === "company") {
          return item?.company?.name || "";
        } else if (orderType === "users" && valueOfCol === "partner") {
          return item?.supplier?.name || "";
        } else if (orderType === "users" && valueOfCol === "status") {
          return item?.status || "";
        } else if (valueOfCol === "parent category" && orderType === "productCategory") {
          return item?.parent?.name || "";
        } else if (valueOfCol === "currency") {
          return item?.currency?.code || item?.currency?.name || "";
        } else if (orderType === "warehouse" && valueOfCol === "types") {
          return (item.types || [])
            .map(t => t?.name || t?.id || t)
            .filter(Boolean)
            .join(", ");
        } else if (orderType === "warehouse" && valueOfCol === "site") {
          return item?.site?.name || "";
        } else if (valueOfCol === "type") {
          return item?.type?.name || "";
        } else if (
          ["product", "supplier", "from facility", "to facility"].includes(valueOfCol) &&
          orderType === "warehouseCostingRange"
        ) {
          switch (valueOfCol) {
            case "product":
              return item?.warehouse_costing?.product?.number || "";
            case "supplier":
              return item?.warehouse_costing?.supplier?.code || "";
            case "from facility":
              return item?.warehouse_costing?.from_warehouse?.code || "";
            case "to facility":
              return item?.warehouse_costing?.to_warehouse?.code || "";

            default:
              return "";
          }
        } else if (
          ["supplier", "from facility", "to facility"].includes(valueOfCol) &&
          orderType === "warehouseCosting"
        ) {
          switch (valueOfCol) {
            case "supplier":
              return item?.supplier?.code || "";
            case "from facility":
              return item?.from_warehouse?.code || "";
            case "to facility":
              return item?.to_warehouse?.code || "";

            default:
              return "";
          }
        } else if (booleanField.includes(valueOfCol)) {
          return item[nameOfField] ? "Yes" : "No";
        } else if (orderType === "customer") {
          switch (valueOfCol) {
            case "sales rep":
              return item.sales_rep ? `${item.sales_rep?.full_name}` : "--";

            case "code":
              return item?.customer_code || "";
            default:
              return item[nameOfField]?.name || item[nameOfField] || "";
          }
        } else if (["buying currency", "selling currency"].includes(valueOfCol)) {
          return item[valueOfCol.split(" ").join("_")]?.name;
        } else if (valueOfCol === "full name" && orderType === "salesRep") {
          return item?.user?.full_name || item?.full_name || "";
        } else if (["system default", "default"].includes(valueOfCol)) {
          return item.is_default ? "Yes" : "No";
        } else if (
          ["country", "channel", "supplying facility"].includes(valueOfCol) &&
          orderType === "pricelist"
        ) {
          return item[0]?.name || "";
        } else if (valueOfCol === "title" && orderType === "promo") {
          return item.code || item.title || "";
        } else if (valueOfCol === "stock zone" && orderType === "workCenter") {
          return item?.zone?.name || "";
        } else if (
          valueOfCol === "facility" &&
          orderType === "allocation" &&
          allocation === "salesOrder"
        ) {
          return item.from_warehouse?.name || "";
        } else if (
          valueOfCol === "sales no" &&
          orderType === "allocation" &&
          allocation === "salesOrder"
        ) {
          return item.sale_order?.name || "";
        } else if (orderType === "receipt") {
          // Receipt list columns map differently from Receipt Adjustment
          // (where "receipt no" is adjusted_from).
          switch (valueOfCol) {
            case "receipt no":
              return item.name || "";
            case "document number":
              return item.document_number || "";
            case "document type":
              return item.document_type?.name || "";
            case "shipment number":
              return item.shipment_number || "";
            case "shipment":
              return item.consignment?.reference || item.consignment?.name || "";
            case "to facility":
              return item.to_warehouse?.name || "";
            case "created by":
              return item.created_by?.full_name || "";
            default:
              return item[nameOfField]?.name || item[nameOfField] || "";
          }
        } else if (orderType === "receiptAdjustment") {
          switch (valueOfCol) {
            case "receipt no":
              return item.adjusted_from?.name || "";
            case "receipt adjustment no":
              return item.name || "";
            case "document no":
              return item.document_number || "";
            case "document type":
              return typeof item.document_type === "string"
                ? item.document_type
                : item.document_type?.name || "";
            case "shipment no":
              return item.consignment?.name || "";
            case "created by":
              return item.created_by?.full_name || "";
            default:
              return item[nameOfField]?.name || item[nameOfField] || "";
          }
        } else if (orderType === "people" && valueOfCol === "name") {
          return item?.full_name || "";
        } else if (orderType === "shipmentV1" && valueOfCol === "shipment no") {
          return item?.name || "";
        } else if (orderType === "asn" && valueOfCol === "asn no") {
          return item?.name || "";
        } else if (orderType === "asn" && valueOfCol === "shipment no") {
          return item?.consignment?.name || "";
        } else if (orderType === "prePayment" && valueOfCol === "purchase orders") {
          const fromAllocations = item?.allocations
            ?.map(alloc => alloc.purchase_order?.name)
            .filter(Boolean);
          if (fromAllocations?.length) {
            return [...new Set(fromAllocations)].join(", ");
          }
          return (
            item?.purchase_orders
              ?.map(po => po.name)
              .filter(Boolean)
              .join(", ") || ""
          );
        }

        return item[nameOfField]?.name || item[nameOfField] || "";
      };

      if (filteredBy === "reset") {
        setSelectedOrder(shipmentOrders);
        return;
      }

      if (filteredBy === "Ascending") {
        if (qtyVal.includes(valueOfCol)) {
          arr.sort((a, b) => selectValue(a) - selectValue(b));
        } else {
          arr.sort((a, b) => selectValue(a).localeCompare(selectValue(b)));
        }
      } else {
        if (qtyVal.includes(valueOfCol)) {
          arr.sort((a, b) => selectValue(b) - selectValue(a));
        } else {
          arr.sort((a, b) => selectValue(b).localeCompare(selectValue(a)));
        }
      }
      setSelectedOrder(arr);
    },

    "product no": filteredBy => {
      const arr = [...shipmentOrders];

      if (filteredBy === "reset") {
        setSelectedOrder(shipmentOrders);
        return;
      }
      const selectValue = item => {
        if (orderType === "inventory") {
          return item.product?.number;
        }
        return item.name;
      };

      if (filteredBy === "Ascending") {
        if (orderType === "shipment") {
          arr.sort((a, b) => selectValue(a) - selectValue(b));
        } else {
          arr.sort((a, b) => selectValue(a).localeCompare(selectValue(b)));
        }
      } else {
        if (orderType === "shipment") {
          arr.sort((a, b) => selectValue(b) - selectValue(a));
        } else {
          arr.sort((a, b) => selectValue(b).localeCompare(selectValue(a)));
        }
      }

      setSelectedOrder(arr);
    },
  };

  const closeSelectSortOption = headerLowerCase => {
    setIsSortModalOpen({
      ...isSortModalOpen,
      [headerLowerCase]: { ...isSortModalOpen[headerLowerCase], isOpen: false },
    });
  };

  useEffect(() => {
    const stored = readListPageSize(orderType);
    if (!stored || !setConsignmentsParamsLocal) return;
    setConsignmentsParamsLocal(state => {
      if (!state || Number(state.limit) === stored) return state;
      return { ...state, limit: stored, offset: 0 };
    });
  }, [orderType, setConsignmentsParamsLocal]);

  const handlePaginationChange = (page, size) => {
    writeListPageSize(orderType, size);
    setConsignmentsParamsLocal(state => ({ ...state, limit: size, offset: page * size - size }));
    const moduleKey = mapItemPerPageWithModule[orderType];
    if (moduleKey) {
      updatePerPage({ limit: { [moduleKey]: size } });
    }
  };

  let isTotalShow = false;

  const linkNameHeader = useMemo(() => `${orderType} no`, [orderType]);

  const resolveListColumnMeta = useCallback(
    columnKey => {
      if (columnKey === linkNameHeader) {
        const label = getListHeaderDisplayLabel(`${orderType} no`);
        return {
          headerUpperCase: `${orderType} no`.toUpperCase(),
          label,
          hasSort: true,
          hasFilter: true,
        };
      }
      const headerUpperCase = tableHeaderList
        ?.find(item => item.toLowerCase() === columnKey)
        ?.toUpperCase();
      const label = headerUpperCase
        ? getListHeaderDisplayLabel(headerUpperCase, HEADER_DISPLAY_OVERRIDES[orderType])
        : columnKey;
      return {
        headerUpperCase,
        label,
        hasSort: headerUpperCase ? isSortable(headerUpperCase) : false,
        hasFilter: headerUpperCase ? isFilterable(headerUpperCase) : false,
      };
    },
    [linkNameHeader, orderType, tableHeaderList, isFilterable, isSortable],
  );

  const persistListColumnWidths = useCallback(
    widths => {
      Object.entries(widths).forEach(([columnName, width]) => {
        if (selectedCurrentView?.id) {
          updateColumnWidth({
            id: selectedCurrentView.id,
            data: {
              column_name: columnName,
              column_width_value: width,
            },
          }).catch(error => {
            console.error(`Failed to update column width for ${columnName}:`, error);
          });
        }
        if (selectedCurrentView?.value === "Default View") {
          const module = orderType === "shipment" ? "consignment" : orderType;
          dispatch(
            setColumnWidthForDefaultViewInLocals({
              column_width: { column_name: columnName, width },
              module,
              allocation,
            }),
          );
        }
      });
    },
    [
      allocation,
      dispatch,
      orderType,
      selectedCurrentView?.id,
      selectedCurrentView?.value,
      updateColumnWidth,
    ],
  );

  const { getHandleProps, dragOverlay } = useColumnResize({
    getWidth: useCallback(
      columnId =>
        columnHeaderWidth[columnId] ||
        afterFirstRenderColumnWidth.current?.[columnId] ||
        COLUMN_CONSTRAINTS.DEFAULT_WIDTH,
      [columnHeaderWidth],
    ),
    getMinWidth: useCallback(
      columnId => {
        const { label, hasSort, hasFilter } = resolveListColumnMeta(columnId);
        return measureHeaderMinWidth({ label, hasSort, hasFilter });
      },
      [resolveListColumnMeta],
    ),
    onCommit: useCallback((columnId, width) => {
      setColumnHeaderWidth(prev => ({ ...prev, [columnId]: width }));
    }, []),
    onPersist: persistListColumnWidths,
    measureFit: useCallback(
      columnId => {
        const { label, hasSort, hasFilter } = resolveListColumnMeta(columnId);
        return measureColumnFitFromDom({
          tableElement: tableRef.current,
          columnKey: columnId,
          headerLabel: label,
          hasSort,
          hasFilter,
        });
      },
      [resolveListColumnMeta],
    ),
  });

  return (
    <ListViewCss className="listView">
      <ColumnResizeDragOverlay dragOverlay={dragOverlay} />
      <div className="tableContainer">
        <table ref={tableRef} data-guide={`list.${orderType}`}>
          <thead>
            <tr>
              <th className="checkBox">
                <div className="headerOfList">
                  <input
                    type="checkbox"
                    value={"all"}
                    onChange={handleSelectOrder}
                    checked={
                      selectedOrderFromList.length > 0 &&
                      selectedOrderFromList.length === shipmentOrders.filter(order => order).length
                    }
                  />
                </div>
              </th>

              {["shipment", "purchase", "transfer"].includes(orderType) && (
                <th className="projectName" data-column-key={linkNameHeader}>
                  <div
                    className="headerOfList"
                    style={{ width: columnHeaderWidth[linkNameHeader] }}
                    ref={el => (headerRefs.current[linkNameHeader] = el)}
                  >
                    {isSortModalOpen["product no"]?.isOpen && (
                      <SelectSortOption
                        valueOfColumn={"product no"}
                        closeModal={() => closeSelectSortOption("product no")}
                        sortOptions={dataForSort["status"]}
                        selectedOption={isSortModalOpen["product no"].value}
                        setIsSortModalOpen={setIsSortModalOpen}
                        handleSortByEta={handleSort["product no"]}
                        setCurrentHeaderForSorting={setCurrentHeaderForSorting}
                      />
                    )}
                    <span onClick={() => handleEtaSort("product no")}>
                      <UpDown
                        color={
                          isSortModalOpen["product no"]?.isAppliedSorting
                            ? SORT_ICON_COLORS.ACTIVE
                            : SORT_ICON_COLORS.INACTIVE
                        }
                      />
                    </span>

                    <div className="headerTextWrapper">
                      <ShowTextOnHover text={getListHeaderDisplayLabel(`${orderType} no`)} />
                    </div>
                    {isAppliedFilter[linkNameHeader]?.isApplied ? (
                      <span
                        className="closeFilter"
                        title="Clear filter"
                        onClick={() => clearFilter(linkNameHeader)}
                      >
                        {selectedOrder.length} <Close />
                      </span>
                    ) : (
                      <FilterIcon onClick={() => handleFilterModalOpen(linkNameHeader)} />
                    )}
                    {isAppliedFilter[linkNameHeader]?.isOpen && (
                      <FilterModal
                        closeFilterModal={() => closeFilterModal(linkNameHeader)}
                        setConsignmentsParamsLocal={setConsignmentsParamsLocal}
                        setIsAppliedFilter={setIsAppliedFilter}
                        headerName={linkNameHeader}
                        orderType={orderType}
                      />
                    )}
                  </div>
                  <ColumnResizeHandle
                    {...getHandleProps(linkNameHeader, {
                      ariaValueNow: columnHeaderWidth[linkNameHeader],
                    })}
                  />
                </th>
              )}

              {tableHeaderList?.map(item => {
                const headerUpperCase = item.toUpperCase();
                const headerLowerCase = item.toLowerCase();

                const displayHeader = getListHeaderDisplayLabel(
                  headerUpperCase,
                  HEADER_DISPLAY_OVERRIDES[orderType],
                );

                const isSorted = currentHeaderForSorting === headerLowerCase;
                const sortIconColor = isSorted
                  ? SORT_ICON_COLORS.ACTIVE
                  : SORT_ICON_COLORS.INACTIVE;

                const sortType = dateFields.includes(headerLowerCase) ? "eta" : "status";
                const sortModalState = isSortModalOpen[headerLowerCase];

                return (
                  <th
                    key={headerUpperCase}
                    className={listOfClassesForHeader[headerUpperCase]}
                    data-column-key={headerLowerCase}
                  >
                    <div
                      className={`headerOfList ${classNameForHeader(headerUpperCase)}`}
                      style={{ width: columnHeaderWidth[headerLowerCase] }}
                      ref={el => (headerRefs.current[headerLowerCase] = el)}
                    >
                      {isSortable(headerUpperCase) && (
                        <span onClick={() => handleEtaSort(headerLowerCase)}>
                          <UpDown color={sortIconColor} />
                        </span>
                      )}

                      {sortModalState?.isOpen && (
                        <SelectSortOption
                          handleSortByEta={handleSort[sortType]}
                          selectedOption={isSorted && sortModalState.value}
                          sortOptions={dataForSort[sortType]}
                          valueOfColumn={headerLowerCase}
                          closeModal={() => closeSelectSortOption(headerLowerCase)}
                          setCurrentHeaderForSorting={setCurrentHeaderForSorting}
                          setIsSortModalOpen={setIsSortModalOpen}
                        />
                      )}

                      <div className="headerTextWrapper">
                        <ShowTextOnHover text={displayHeader} />
                      </div>

                      {isFilterable(headerUpperCase) &&
                        (isAppliedFilter[headerLowerCase]?.isApplied ? (
                          <span
                            className="closeFilter"
                            title="Clear filter"
                            onClick={() => clearFilter(headerLowerCase)}
                          >
                            {selectedOrder.length} <Close />
                          </span>
                        ) : (
                          <FilterIcon onClick={e => handleFilterModalOpen(headerLowerCase, e)} />
                        ))}

                      {isAppliedFilter[headerLowerCase]?.isOpen &&
                        ![
                          "date of transaction",
                          "eta",
                          "etd",
                          "ata",
                          "atd",
                          "order date",
                          "create date",
                          "date of creation",
                          "posting date",
                          "req date",
                          "from",
                          "to",
                          "bill date",
                          "due date",
                          "date",
                          "credit date",
                          "date paid",
                          "validity date",
                          "expiry date",
                          "requested ship date",
                          "pick date",
                          "planned start",
                          "required by",
                          "est completion",
                        ].includes(headerLowerCase) &&
                        (isSearchFilterField(orderType, headerLowerCase) ? (
                          <SearchFilterModal
                            headerName={headerLowerCase}
                            config={getSearchFilterConfig(orderType, headerLowerCase)}
                            consignmentsParams={consignmentsParams}
                            setConsignmentsParamsLocal={setConsignmentsParamsLocal}
                            isAppliedFilter={isAppliedFilter}
                            setIsAppliedFilter={setIsAppliedFilter}
                            closeFilterModal={() => closeFilterModal(headerLowerCase)}
                            positionForFilter={positionForFilter}
                          />
                        ) : (
                          <FilterModal
                            closeFilterModal={() => closeFilterModal(headerLowerCase)}
                            setConsignmentsParamsLocal={setConsignmentsParamsLocal}
                            setIsAppliedFilter={setIsAppliedFilter}
                            headerName={headerLowerCase}
                            positionForFilter={positionForFilter}
                            orderType={orderType}
                            allocation={allocation}
                          />
                        ))}

                      {[
                        "date of transaction",
                        "eta",
                        "etd",
                        "ata",
                        "atd",
                        "order date",
                        "posting date",
                        "create date",
                        "date of creation",
                        "req date",
                        "from",
                        "to",
                        "bill date",
                        "due date",
                        "date",
                        "credit date",
                        "date paid",
                        "validity date",
                        "expiry date",
                        "requested ship date",
                        "pick date",
                        "planned start",
                        "required by",
                        "est completion",
                      ].includes(headerLowerCase) &&
                        isAppliedFilter[headerLowerCase]?.isOpen && (
                          <PopupModal
                            closeModal={() => closeFilterModal(headerLowerCase)}
                            styleOfModal={styleOfDatePopup}
                          >
                            <DateTimeFilterHeaderV2
                              id={
                                (orderType === "asn" &&
                                  headerLowerCase === "order date" &&
                                  "date_of_creation") ||
                                (orderType === "purchase" &&
                                  headerLowerCase === "eta" &&
                                  "estimated_date") ||
                                (orderType === "shipmentLeg" &&
                                  headerLowerCase === "eta" &&
                                  "estimated_arrival_at") ||
                                (orderType === "shipmentLeg" &&
                                  headerLowerCase === "etd" &&
                                  "estimated_departure_at") ||
                                (orderType === "shipmentLeg" &&
                                  headerLowerCase === "ata" &&
                                  "actual_arrival_at") ||
                                (orderType === "shipmentLeg" &&
                                  headerLowerCase === "atd" &&
                                  "actual_departure_at") ||
                                ([
                                  "billChargeType",
                                  "productCategory",
                                  "productAlias",
                                  "pack",
                                  "forecast",
                                  "stockZone",
                                  "stockLocation",
                                  "warehouseCosting",
                                  "warehouseCostingRange",
                                  "transactionReason",
                                  "standardCost",
                                  "taxRate",
                                  "site",
                                  "workCenter",
                                  "people",
                                  "users",
                                  "boms",
                                  "customerTag",
                                  "prePayment",
                                  "stockReplenishment",
                                  "inventoryDetail",
                                  "customer",
                                  "salesRep",
                                  "equipmentType",
                                  "equipment",
                                  "saleInvoice",
                                  "unitOfMeasure",
                                  "journalLine",
                                ].includes(orderType) &&
                                  (headerLowerCase === "create date" ||
                                    headerLowerCase === "date of creation" ||
                                    headerLowerCase === "joined" ||
                                    headerLowerCase === "last login") &&
                                  (headerLowerCase === "last login"
                                    ? "last_login"
                                    : "date_of_creation")) ||
                                mapDateFilterId[headerLowerCase] ||
                                "eta"
                              }
                              title={headerUpperCase}
                              setConsignmentsParamsLocal={setConsignmentsParamsLocal}
                              setIsAppliedFilter={setIsAppliedFilter}
                              positionForFilter={positionForFilter}
                            />
                          </PopupModal>
                        )}
                    </div>
                    <ColumnResizeHandle
                      {...getHandleProps(headerLowerCase, {
                        ariaValueNow: columnHeaderWidth[headerLowerCase],
                      })}
                    />
                  </th>
                );
              })}
              <th key={"emptyColRef"} className="emptyColumn" ref={emptyColRef}></th>
            </tr>
          </thead>
          {selectedOrder.length === 0 ? (
            <tbody>
              <tr>
                <td>
                  <EmptyStateComp
                    title={emptyState.title}
                    description={emptyState.description}
                    actions={emptyState.actions}
                  />
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody onClick={handleSelectOrder}>
              {tableHeaderList &&
                selectedOrder?.map(order => {
                  return (
                    <tr
                      key={listRowIdentity(order)}
                      className={
                        selectedOrderId.includes(listRowIdentity(order))
                          ? "selectedRow tableRow"
                          : "tableRow"
                      }
                    >
                      <td key="check box " className="checkBox">
                        <div className="headerOfList">
                          <input
                            type="checkbox"
                            onChange={demoFn}
                            value={JSON.stringify(order)}
                            checked={selectedOrderId.includes(listRowIdentity(order))}
                          />
                        </div>
                      </td>

                      {[
                        "purchase",
                        "shipment",
                        "transfer",
                        "quote",
                        "salesOrder",
                        "picklist",
                        "delivery",
                      ].includes(orderType) && (
                        <TextOnHoverWhenAdjust
                          text={
                            <Link
                              to={`/${detailPageUrl[orderType]}/${order.id}`}
                              style={{ color: "#1249bf", fontWeight: 500 }}
                            >
                              {" "}
                              {order.name}
                            </Link>
                          }
                          widthOfElement={columnHeaderWidth[linkNameHeader] || 100}
                        />
                      )}

                      {tableDataElements({
                        order,
                        orderType,
                        tableHeaderList,
                        allocation,
                        columnHeaderWidth,
                        formattedAmount,
                        onTrackingUpdated,
                        onOpenIncomingDrawer,
                        recordActionHandlers,
                      })}

                      <td className="emptyColumn"></td>
                    </tr>
                  );
                })}

              {orderType === "allocation" && (
                <TotalTrCss
                  lengthOfOrder={shipmentOrders?.length < 8 ? true : false}
                  className={`total moreItems`}
                >
                  <td className="checkBox"></td>
                  {tableHeaderList?.map((item, ind) => {
                    if (!isTotalShow && qtyItemsForAllocation.includes(tableHeaderList[ind + 1])) {
                      isTotalShow = true;
                      return (
                        <td key={item} className={`${listOfClasses[item]} totalText`}>
                          {" "}
                          Total
                        </td>
                      );
                    }

                    if (
                      ["salesOrder", "manufacturingOrder"].includes(allocation) &&
                      item === "REQUIRED"
                    ) {
                      return (
                        <td key={item} className={`${listOfClasses[item]} totalValue`}>
                          {formattedAmount(totalCount["req_quantity"] || 0)}
                        </td>
                      );
                    }

                    const totalCellValue = totalCount[countMapWithHeader[item]];
                    const hasTotalValue = totalCellValue === 0 || Boolean(totalCellValue);
                    return (
                      <td
                        key={item}
                        className={`${listOfClasses[item]} ${hasTotalValue ? "totalValue" : ""}`}
                      >
                        {hasTotalValue && formattedAmount(totalCellValue)}
                      </td>
                    );
                  })}
                </TotalTrCss>
              )}
            </tbody>
          )}
        </table>
      </div>

      <ListBoardPagination
        current={currentPage}
        total={totalItems}
        pageSize={consignmentsParams.limit}
        onChange={handlePaginationChange}
      />
    </ListViewCss>
  );
};

export { ListView };

const styleOfDatePopup = { display: "contents" };

export const ListViewCss = styled.div`
  .headerTextWrapper {
    flex: 0 1 auto;
    min-width: 24px;
  }

  flex: 1;
  padding: 0 4px 50px;

  > .tableContainer {
    overflow-x: auto;
    height: calc(100vh - 190px);
    border-bottom: 1px solid #e9edf5;
    table,
    thead,
    tbody {
      width: 100%;
      font-size: 14px;
    }
    > table {
      max-width: max-content;

      > thead {
        position: sticky;
        top: 0;
        background-color: #fff;
        z-index: 1;
      }
      .leftAlign {
        text-align: left;
      }
      .centerAlign {
        justify-content: center;
      }
      .rightAlign {
        text-align: right;
        justify-content: flex-end;
      }

      > thead,
      tbody > tr {
        .checkBox {
          padding: 0 16px 0 20px;
          .headerOfList {
            display: flex;
            align-items: center;
          }
          input {
            width: 16px;
            height: 16px;
          }
        }
        td,
        th {
          white-space: nowrap;
        }
        .emptyColumn {
          width: 100%;
        }

        .rightSide {
          right: 0;
        }
      }
      > thead > tr {
        justify-content: space-between;
        align-items: center;
        height: 34px;
        margin-right: 12px;
        border-bottom: 1.6px solid var(--Gray-50, #e9edf5);

        > th {
          align-items: center;
          gap: 8px;
          padding: 0 16px;
          ${tableHeaderTextCss}
          position: relative;
          .headerOfList {
            display: flex;
            gap: 8px;
            align-items: center;
            .headerName {
              overflow: hidden;
              text-overflow: ellipsis;
            }
            > svg {
              min-width: 13px;
            }
          }

          svg {
            cursor: pointer;
          }
        }
        > .checkBox {
          > input {
            width: 16px;
            height: 16px;
          }
        }

        .closeFilter {
          display: flex;
          padding: 0px 8px;
          justify-content: center;
          align-items: center;
          gap: 4px;
          border-radius: 100px;
          background: var(--Peach, #f3d9da);
          cursor: pointer;
        }
        .projectName {
          position: relative;
        }
        > .rightAlign {
          .headerOfList {
            text-align: right;
            justify-content: right;
          }
        }
        /* 16px cell pad + 24px EditAllocatedQty iconSlot */
        > .allocatedHeader {
          padding-right: 40px;
        }
        > .centerAlign,
        .headerOfList.centerAlign {
          justify-content: center;
          margin: 0 auto;
          text-align: center;
        }
        .showDateFilter {
          position: absolute;
          top: 40px;
          z-index: 1000;
          form .btn-for-date-filter {
            button {
              color: var(--White, #fff);
              height: 32px;
              line-height: 16px;
              padding: 4px 16px !important;
              border-radius: 4px;
              background-color: var(--BLUE, #2060ea);
              border: 1px solid transparent;
            }
            .reset-btn {
              color: var(--GREY, #4a4a68);
              background-color: var(--MIST, #f7f8f9);
            }
          }
        }
      }
      > tbody {
        overflow-y: auto;
        scrollbar-width: thin;

        > .selectedRow {
          background-color: var(--Pearl, #ecf1f4);
        }
        > .tableRow {
          height: 48px;
        }
        > tr {
          justify-content: space-between;
          align-items: center;
          height: 48px;
          ${tableCellTextCss}

          > td.rightAlign {
            ${tableNumericCss}
          }
          .productName {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            display: block;
          }

          > td {
            padding: 0 16px;
          }

          > .checkBox {
            display: flex;
            height: 46px;

            > input {
              width: 16px;
              height: 16px;
            }
          }

          .projectName {
            color: #1249bf;
            font-weight: 500;
            cursor: pointer;
            a {
              color: #1249bf;
            }
          }
          .ordNo {
            color: #1249bf;
            font-weight: 500;
            cursor: pointer;
            a {
              color: #1249bf;
            }
          }
          .centerAlign {
            margin: 0 auto;
            text-align: center;
          }

          .status {
            .statusBox {
              margin: 0 auto;
            }
          }
          .estimatedDate {
            font-weight: 400;
            display: flex;
            align-items: center;
            gap: 6px;
          }
          .userPic {
            font-weight: 400;
          }

          .wareHouse {
            font-weight: 400;
          }
        }

        > tr:nth-child(even) {
          background-color: var(--CLOUD, #fcfdfe);
          border-top: 1px solid var(--Gray-50, #e9edf5);
          border-bottom: 1.6px solid var(--Gray-50, #e9edf5);
        }
        > tr.selectedRow {
          background-color: var(--Pearl, #ecf1f4);
        }
      }
    }
  }
`;

const TotalTrCss = styled.tr`
  ${detailTotalsFooterRowCss}
  ${detailTotalsFooterBarTopCss}
  position: ${props => (props.lengthOfOrder ? "static" : "sticky")};
  bottom: 0;
  z-index: 2;
  box-shadow: ${props => (props.lengthOfOrder ? "none" : DETAIL_TOTALS_FOOTER_SHADOW)};

  > td {
    background-color: ${DETAIL_TOTALS_FOOTER_BG};
    padding: 0 16px;
  }

  > .totalText {
    ${detailTotalsFooterLabelCss}
  }

  > .totalValue {
    ${detailTotalsFooterValueCss}
  }

  /* 16px cell pad + 24px EditAllocatedQty iconSlot */
  > .allocatedTotal {
    padding-right: 40px;
  }
`;
